/**
 * Prefill the "Complete the document" form from data the platform already has.
 *
 * The fill-in form is built from the linter's placeholder list, which is purely
 * a string scan of the rendered draft — it cannot tell a fact the user must
 * supply (meeting time) from one already sitting in the company record (the
 * registered address). Without this resolver every field renders blank and the
 * user retypes values the document is already printing on the same page.
 *
 * DELIBERATELY CONSERVATIVE. A resolution is a filing document: a confidently
 * wrong auto-filled value is far worse than an empty box. Only placeholders
 * whose meaning is unambiguous get filled — see NEVER_PREFILL below.
 */

// resolveFieldType lives with the controls. factsFields does NOT import this
// module, so this direction creates no cycle.
import { resolveFieldType } from './factsFields';

// Indian states / UTs, longest-first so "Andhra Pradesh" wins over a substring
// match and "West Bengal" isn't shadowed by "Bengal".
const STATES = [
  'Andaman and Nicobar Islands', 'Arunachal Pradesh', 'Himachal Pradesh', 'Madhya Pradesh',
  'Jammu and Kashmir', 'Dadra and Nagar Haveli', 'Andhra Pradesh', 'Uttar Pradesh',
  'Daman and Diu', 'Chhattisgarh', 'Maharashtra', 'West Bengal', 'Lakshadweep',
  'Puducherry', 'Uttarakhand', 'Tamil Nadu', 'Telangana', 'Rajasthan', 'Karnataka',
  'Jharkhand', 'Chandigarh', 'Meghalaya', 'Nagaland', 'Mizoram', 'Manipur', 'Haryana',
  'Gujarat', 'Tripura', 'Ladakh', 'Punjab', 'Odisha', 'Kerala', 'Sikkim', 'Assam',
  'Bihar', 'Delhi', 'Goa',
];

/** Pull a state name out of a free-text registered address. '' when none matches. */
const stateFromAddress = (address = '') => {
  const hay = String(address || '');
  const hit = STATES.find((s) => new RegExp(`\\b${s}\\b`, 'i').test(hay));
  return hit || '';
};

/** 1500000 → "₹15,00,000" (Indian grouping: last 3, then pairs). */
const formatINR = (value) => {
  const n = Number(String(value ?? '').replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n) || n <= 0) return '';
  const [whole] = String(Math.round(n)).split('.');
  const last3 = whole.slice(-3);
  const rest = whole.slice(0, -3);
  const grouped = rest ? `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}` : last3;
  return `₹${grouped}`;
};

/**
 * "23 July 2026" / "2026-07-23" → "Thursday, 23 July 2026".
 * Returns '' when the date can't be parsed — never a half-built string.
 */
const dayAndDate = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const weekday = d.toLocaleDateString('en-IN', { weekday: 'long' });
  const rest = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  return `${weekday}, ${rest}`;
};

/** Read "Date of Meeting : 23 July 2026" out of the draft's plain text. */
const meetingDateFromDraft = (draft) => {
  const text = String(draft?.plainText || '');
  const m = text.match(/Date of Meeting\s*:?\s*([^\n|]{4,40}?)\s*(?:\n|CIN|Venue|Subject|$)/i);
  const value = m ? m[1].trim() : '';
  // An unfilled draft prints "Date of Meeting : [DATE]" — extracting that
  // token would seed the form with the literal string "[DATE]" (and suppress
  // the date picker, which only renders on empty/ISO values).
  return /\[[^\]]*\]/.test(value) ? '' : value;
};

// Person-identifying placeholders. These are never auto-filled: in an
// appointment resolution they name the person being APPOINTED, who is by
// definition not yet in company.directors, so seeding them from the existing
// board would put a wrong name and DIN into a document headed for the ROC.
// They get a PICKER instead (see directorOptions) — one click for the
// authorisation case, an explicit choice for the appointment case.
const PERSON_NAME = /^\[(name|name of director|director name|director \d+ name|authorised (director|signatory))\]$/i;
const PERSON_DIN = /^\[din(\s*\d+)?\]$/i;

const NEVER_PREFILL = /^\[(__|time)\]$/i;

// Case-SENSITIVE block. Mixed-case [Address] is the appointee's residential
// address written inline by the model ("residing at [Address]"); the all-caps
// [ADDRESS] is document chrome for the registered office. Only the latter is
// safe to fill, so this one exact token cannot fold into a /i test.
const NEVER_PREFILL_EXACT = new Set(['[Address]', '[Date]']);

/**
 * The company's serving directors as picker options. Each name option carries
 * `fills` so choosing a director also drops their DIN into the DIN field —
 * the pair always stays consistent, which a free-text box cannot guarantee.
 */
const activeDirectors = (company) =>
  (company?.directors || []).filter((d) => d && d.name && !d.cessation);

/**
 * The director both person pickers open on. Preferring one that HAS a DIN
 * matters: a director without one is dropped from the DIN list, so defaulting
 * each list to its own index 0 could pair one director's name with another's
 * DIN. Resolving a single director here keeps the pair honest.
 * A signatory outranks a non-signatory — they're who actually signs.
 */
export const defaultDirector = (company) => {
  const active = activeDirectors(company);
  return active.find((d) => d.din && d.signatory)
    || active.find((d) => d.din)
    || active[0]
    || null;
};

export function directorOptions(company, kind) {
  const active = activeDirectors(company);
  if (!active.length) return [];
  return active.map((d) => {
    const desig = d.designation ? ` — ${d.designation}` : '';
    return kind === 'din'
      ? { value: d.din || '', label: `${d.din || '(no DIN)'} — ${d.name}`, fills: { name: d.name } }
      : { value: d.name, label: `${d.name}${desig}${d.din ? ` (DIN ${d.din})` : ''}`, fills: d.din ? { din: d.din } : {} };
  }).filter((o) => o.value);
}

/** Which person field, if any, a placeholder token represents. */
export const personFieldKind = (key) =>
  (PERSON_NAME.test(key) ? 'name' : PERSON_DIN.test(key) ? 'din' : null);

/**
 * Resolve what we can for a list of placeholder tokens.
 *
 * @param {string[]} placeholders  e.g. ['[STATE]', '[VENUE]', '[TIME]']
 * @param {object}   ctx
 * @param {object}   [ctx.company]  the active company (canonical contract shape)
 * @param {object}   [ctx.draft]    the draft the form belongs to (for its meeting date)
 * @returns {Object<string,string>} placeholder → value, omitting anything unresolved
 */
export function prefillPlaceholders(placeholders = [], { company, draft } = {}) {
  const c = company || {};
  const address = c.registeredAddress || c.address || '';
  const state = c.state || stateFromAddress(address) || c.jurisdiction || '';

  const RESOLVERS = {
    '[STATE]': () => state,
    '[VENUE]': () => address,
    '[ADDRESS]': () => address,
    '[REGISTERED OFFICE]': () => address,
    '[COMPANY NAME]': () => c.name || '',
    '[CIN]': () => c.cin || '',
    '[CIN / LLPIN]': () => c.cin || '',
    '[ROC]': () => c.roc || c.rocOffice || '',
    '[STATUTORY AUDITOR]': () => c.auditor || '',
    '[AUTH CAP]': () => formatINR(c.authorisedCapital),
    '[PAID-UP CAP]': () => formatINR(c.paidUpCapital),
    '[DAY, DATE]': () => dayAndDate(meetingDateFromDraft(draft)),
    '[DAY]': () => dayAndDate(meetingDateFromDraft(draft)).split(',')[0] || '',
    '[DATE]': () => meetingDateFromDraft(draft),
    // [TIME] is deliberately NOT resolved (NEVER_PREFILL): seeding the current
    // clock time invented a fact ("12:54 A.M." in a board resolution) — an
    // empty TimePicker beats a confidently wrong value.
  };

  const out = {};
  for (const ph of placeholders) {
    const key = String(ph || '').trim();
    if (!key || NEVER_PREFILL.test(key) || NEVER_PREFILL_EXACT.has(key)) continue;
    // Person fields get a picker, never a guessed value.
    if (personFieldKind(key)) continue;
    // Case-insensitive lookup so [State] and [STATE] both resolve.
    const match = Object.keys(RESOLVERS).find((k) => k.toLowerCase() === key.toLowerCase());
    if (!match) continue;
    const value = RESOLVERS[match]();
    if (value) out[key] = value;
  }
  return out;
}

/**
 * Attach a `value` to each field the resolver could answer, and `options` to
 * the person fields so they render as a director picker instead of a text box.
 *
 * Name and DIN fields are cross-linked: each option's `fills` names the sibling
 * field's ROLE ('name' / 'din'), which is resolved here to the actual
 * placeholder key present in THIS form (the token varies — [NAME], [DIN 1],
 * [Name of Director]…). A picker with no sibling present simply fills itself.
 */
/**
 * Today / now, as a date or time field's opening value.
 *
 * This sits on the RIGHT side of a line this codebase draws hard. The rule that
 * matters — statedDateOnly, hasUserDateReference — is that the ASSISTANT must
 * never put a date on a document the user did not give: a resolution certified
 * as passed on a day nobody named is a fabricated fact, and it is fabricated
 * silently, inside a finished document.
 *
 * A picker's opening value is the opposite of silent. It sits in a control the
 * user is looking at, in a form they have to submit, next to every other detail
 * they are entering — and most meetings being minuted are today's. Making them
 * type today's date to say "today" is friction with nothing bought by it.
 *
 * What this must never become: a value that reaches a document without passing
 * through the user's eyes. It is only ever applied to a field rendered in a
 * form; nothing here touches a draft directly.
 */
const pad2 = (n) => String(n).padStart(2, '0');

const todayValue = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const nowValue = () => {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** The opening value for an unanswered date/time field, or '' for anything else. */
export function nowDefault(field = {}) {
  const type = resolveFieldType(field);
  if (type === 'date') return todayValue();
  if (type === 'time') return nowValue();
  return '';
}

export function withPrefilledValues(fields = [], ctx = {}) {
  const filled = prefillPlaceholders(fields.map((f) => f.key), ctx);
  const keyForRole = {
    name: fields.find((f) => personFieldKind(f.key) === 'name')?.key,
    din: fields.find((f) => personFieldKind(f.key) === 'din')?.key,
  };

  // Both person pickers open on the SAME director, so the name and DIN a user
  // submits without touching the form are still a matched pair.
  const preselect = defaultDirector(ctx.company);

  return fields.map((f) => {
    const kind = personFieldKind(f.key);
    if (kind) {
      const options = directorOptions(ctx.company, kind).map((o) => ({
        value: o.value,
        label: o.label,
        // role → sibling placeholder key, dropping roles not in this form.
        fills: Object.fromEntries(
          Object.entries(o.fills || {})
            .map(([role, val]) => [keyForRole[role], val])
            .filter(([k]) => k),
        ),
      }));
      if (!options.length) return f;
      const value = kind === 'din' ? (preselect?.din || '') : (preselect?.name || '');
      // Only preselect a value the list actually offers.
      return options.some((o) => o.value === value)
        ? { ...f, type: 'person', options, value }
        : { ...f, type: 'person', options };
    }
    // A value the platform ALREADY HOLDS is not a question. Marking where a
    // value came from lets the form use it without putting it in front of the
    // user: asking someone to confirm their own registered address, which the
    // document is printing at the top of the same page, is work with nothing
    // bought by it. Only 'record' is hidden — a picker is a CHOICE and a
    // clock default is a guess, and both still belong on screen.
    if (filled[f.key]) return { ...f, value: filled[f.key], source: 'record' };
    // Nothing on record answers this field. If it is a date or a time, open the
    // picker on NOW rather than blank — see todayValue/nowValue for why that is
    // a default and not an invention.
    const seeded = nowDefault(f);
    return seeded ? { ...f, value: seeded, source: 'now' } : f;
  });
}

/**
 * Fill the director list into fields the MODEL asked for as `type: 'person'`
 * (request_facts). The model names the role it needs a person for; only the
 * client knows who is actually on this company's board, so the options are
 * attached here rather than invented in the tool call.
 *
 * A DIN field asked for alongside is cross-linked the same way the placeholder
 * path does it — picking a director fills their DIN so the pair can't drift.
 * With no company, or no directors on record, the fields stay free text.
 */
export function attachPersonOptions(fields = [], company) {
  if (!fields.some((f) => f && f.type === 'person')) return fields;
  const options = directorOptions(company, 'name');
  if (!options.length) return fields;
  // The model's own key for the DIN, if it asked for one ("din", "director_din").
  const dinKey = fields.find((f) => /(^|_)din(_|$)/i.test(f.key || '') || /\bdin\b/i.test(f.label || ''))?.key;
  const linked = options.map((o) => ({
    value: o.value,
    label: o.label,
    fills: dinKey && o.fills?.din ? { [dinKey]: o.fills.din } : {},
  }));
  return fields.map((f) => (f.type === 'person' ? { ...f, options: linked } : f));
}

// The hidden key a company pick writes its CIN into. The drafting tools need a
// CIN, but making the user read one is pointless — they pick a NAME and the
// identifier rides along, the same trick the director picker uses for DINs.
export const COMPANY_CIN_KEY = '__company_cin';

/**
 * Fill the user's own companies into fields the MODEL asked for as
 * `type: 'company'` (request_facts) — "which company is this document for?".
 *
 * The model asks the question; only the client knows the portfolio, so the list
 * is attached here rather than named in the tool call. That is also the
 * security posture the rest of the chat uses: the model never enumerates the
 * user's companies into a prompt, and whatever comes back is re-checked
 * server-side by assertCompanyAccess before a single record is read.
 *
 * Each option carries the CIN in `fills` so the submitted answer identifies the
 * company exactly — two companies can share a trading name, and a document
 * drafted for the wrong one of those is the failure this whole flow exists to
 * prevent.
 */
export function attachCompanyOptions(fields = [], companies = []) {
  if (!fields.some((f) => f && f.type === 'company')) return fields;
  const options = (Array.isArray(companies) ? companies : [])
    .filter((c) => c && c.cin && c.name)
    .map((c) => ({
      value: c.name,
      // The CIN rides in the label too: it is what tells two similarly-named
      // companies apart at the moment of choosing, which is the only moment
      // that matters.
      label: `${c.name} — ${c.cin}`,
      fills: { [COMPANY_CIN_KEY]: String(c.cin).toUpperCase() },
    }));
  if (!options.length) return fields;
  return fields.map((f) => (f.type === 'company' ? { ...f, options } : f));
}

/**
 * The company a submitted facts form chose, or null.
 * Reads the hidden CIN key that attachCompanyOptions' `fills` wrote.
 */
export function pickedCompanyFrom(fields = [], values = {}) {
  const field = (fields || []).find((f) => f && f.type === 'company');
  if (!field) return null;
  const cin = String(values[COMPANY_CIN_KEY] || '').trim().toUpperCase();
  const name = String(values[field.key] || '').trim();
  return cin && name ? { cin, name } : null;
}

// The sentence the composer sends when a company is picked, and the pattern
// that reads it back. The CIN is what makes this recoverable: a restored
// conversation has only its transcript, so the binding has to be legible IN the
// transcript rather than held in state that a reload throws away. Same
// principle as the details-card confirmation sentence (threadFacts.js).
export const companyChoiceSentence = ({ name, cin }) =>
  `Use ${name} (CIN ${cin}) for this document.`;

const COMPANY_CHOICE_RE = /\(CIN\s+([A-Z0-9][A-Z0-9-]{5,24})\)/gi;

/**
 * The company this conversation is bound to, read back from its transcript.
 * The LAST pick wins — a user who changes their mind mid-thread means it.
 * Returns { cin } or null.
 */
export function threadCompanyFrom(messages = []) {
  let cin = null;
  for (const m of messages || []) {
    if (!m || m.role !== 'user') continue;
    const text = String(m.content || '');
    COMPANY_CHOICE_RE.lastIndex = 0;
    let match;
    // eslint-disable-next-line no-cond-assign
    while ((match = COMPANY_CHOICE_RE.exec(text)) !== null) cin = match[1].toUpperCase();
  }
  return cin ? { cin } : null;
}

/**
 * Fields the company record already answered — the ones not worth asking about.
 *
 * A picker is excluded even when a director is preselected: choosing WHICH
 * director acts is a decision, not a lookup, and the record cannot make it.
 */
export const isRecordAnswered = (field = {}) => field.source === 'record' && !field.options;

/** The visible half of an ask form — what the user actually has to answer. */
export const askableFields = (fields = []) => (fields || []).filter((f) => !isRecordAnswered(f));
